import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from '@/contexts/AuthContext';
import { LoginForm } from '@/components/LoginForm';
import { UserProfile } from '@/components/UserProfile';

/**
 * Error fallback component
 */
function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
        <p className="text-gray-700 mb-4">{error.message}</p>
        <button onClick={resetErrorBoundary} className="btn btn-primary">
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * Loading component
 */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <svg
          className="animate-spin h-12 w-12 text-primary-600 mx-auto mb-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Main application component
 */
function App() {
  const { isAuthenticated, isLoading, user, error, signInWithMagicLink, signOut } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {isAuthenticated && user ? (
          <UserProfile user={user} onSignOut={signOut} />
        ) : (
          <LoginForm onSubmit={signInWithMagicLink} isLoading={isLoading} error={error} />
        )}
      </ErrorBoundary>
    </div>
  );
}

export default App;
